import type { Int64 } from "../../api";
import { addAmounts, compareAmounts, subAmounts } from "../../format";

/**
 * Le scénario interactif de la section « grand livre » de la vitrine : un
 * portefeuille jouet où l'on achète, réserve, impute et clôture, et où la
 * somme des quatre seaux vaut toujours ce qui est entré.
 *
 * Module pur, sans React : c'est lui qu'on teste. Les montants sont des
 * `Int64` en tokens entiers (pas des centièmes : c'est une illustration, pas
 * un solde), manipulés par les mêmes helpers BigInt que le reste du front.
 */
export type Scenario = {
  available: Int64;
  reserved: Int64;
  consumed: Int64;
  expired: Int64;
  /** Tout ce qui est entré depuis le départ. */
  entered: Int64;
  /** Le reliquat réservé de la mission ouverte, ou nul sans mission. */
  mission: Int64 | null;
};

export const SCENARIO_INITIAL: Scenario = {
  available: "0",
  reserved: "0",
  consumed: "0",
  expired: "0",
  entered: "0",
  mission: null,
};

/** Les montants des boutons : un pack, une mission, trois jours. */
export const PACK = "500";
export const MISSION = "300";
export const TROIS_JOURS = "120";

/** Achat d'un pack : les tokens entrent dans le disponible. */
export function acheter(s: Scenario, montant: Int64): Scenario {
  return { ...s, available: addAmounts(s.available, montant), entered: addAmounts(s.entered, montant) };
}

/**
 * Commande d'une mission : réserve l'enveloppe. Refuse — et rend l'état tel
 * quel — si une mission est déjà ouverte ou si le disponible ne suffit pas :
 * c'est exactement le refus du serveur.
 */
export function reserver(s: Scenario, montant: Int64): Scenario {
  if (s.mission !== null || compareAmounts(s.available, montant) < 0) {
    return s;
  }
  return {
    ...s,
    available: subAmounts(s.available, montant),
    reserved: addAmounts(s.reserved, montant),
    mission: montant,
  };
}

/** Imputation de jours validés : du réservé vers le consommé, dans l'enveloppe. */
export function imputer(s: Scenario, montant: Int64): Scenario {
  if (s.mission === null || compareAmounts(s.mission, montant) < 0) {
    return s;
  }
  return {
    ...s,
    reserved: subAmounts(s.reserved, montant),
    consumed: addAmounts(s.consumed, montant),
    mission: subAmounts(s.mission, montant),
  };
}

/** Clôture : le reliquat de l'enveloppe revient au disponible. */
export function cloturer(s: Scenario): Scenario {
  if (s.mission === null) {
    return s;
  }
  return {
    ...s,
    reserved: subAmounts(s.reserved, s.mission),
    available: addAmounts(s.available, s.mission),
    mission: null,
  };
}

/** La somme des quatre seaux. */
export function sommeDesSeaux(s: Scenario): Int64 {
  return addAmounts(s.available, s.reserved, s.consumed, s.expired);
}

/** L'invariant du grand livre : la somme des seaux vaut le total entré. */
export function invariantTenu(s: Scenario): boolean {
  return compareAmounts(sommeDesSeaux(s), s.entered) === 0;
}

/** Ce que l'état permet, pour désactiver les boutons plutôt que refuser. */
export function gestesPossibles(s: Scenario): { reserver: boolean; imputer: boolean; cloturer: boolean } {
  return {
    reserver: s.mission === null && compareAmounts(s.available, MISSION) >= 0,
    imputer: s.mission !== null && compareAmounts(s.mission, TROIS_JOURS) >= 0,
    cloturer: s.mission !== null,
  };
}
